import type { MeterSourceType } from "@/models/meterSourceType"
import type { Option } from "@/nzds/inputs/inputs.types"
import { getDisplayValue } from "@/utils/value"

const resourceCodeLabelKeys = {
  electricity: "fields.resource.items.electricity",
  fuel: "fields.resource.items.fuel",
  natural_gas: "fields.resource.items.naturalGas",
  refrigerant: "fields.resource.items.refrigerants",
  water: "fields.resource.items.water",
} as const

type ResourceCodeLabelKey =
  (typeof resourceCodeLabelKeys)[keyof typeof resourceCodeLabelKeys]

export interface MeterResourceSource {
  meterSourceType?: {
    resourceTypeCode?: MeterSourceType["resourceTypeCode"] | null
  } | null
}

const isKnownResourceCode = (
  code: string
): code is keyof typeof resourceCodeLabelKeys =>
  Object.hasOwn(resourceCodeLabelKeys, code)

const formatResourceCode = (code: string): string =>
  code
    .split("_")
    .filter((part) => part !== "")
    .map((part) => {
      const [first = "", ...rest] = part
      return `${first.toUpperCase()}${rest.join("")}`
    })
    .join(" ")

export const getDistinctMeterResourceCodes = (
  meters: readonly MeterResourceSource[]
): string[] => {
  const codes: string[] = []
  const seen = new Set<string>()

  meters.forEach((meter) => {
    const code = meter.meterSourceType?.resourceTypeCode?.trim()
    if (!code || seen.has(code)) {
      return
    }

    seen.add(code)
    codes.push(code)
  })

  return codes
}

const labelResourceCode = (
  code: string,
  translate: (key: ResourceCodeLabelKey) => string
): string => {
  const normalized = code.trim()
  if (!isKnownResourceCode(normalized)) {
    return formatResourceCode(normalized)
  }

  return translate(resourceCodeLabelKeys[normalized])
}

export const displayResourceCode = (
  code: string | null | undefined,
  translate: (key: ResourceCodeLabelKey) => string
): string => {
  const normalized = code?.trim() ?? ""
  if (!normalized) {
    return getDisplayValue(code)
  }

  return labelResourceCode(normalized, translate)
}

export const buildResourceFilterOptions = (
  codes: readonly string[],
  translate: (key: ResourceCodeLabelKey) => string
): Option[] =>
  codes.map((code) => ({
    label: labelResourceCode(code, translate),
    value: code,
  }))
